<script lang="ts" setup>
import type { IButtonProps } from '~/interfaces/components/button';

const props = withDefaults(defineProps<IButtonProps>(), {
  icon: false,
  class: '',
  disabled: false,
  nativeType: 'button',
});

const defaults =
  'flex items-center font-bold hover:cursor-pointer hover:shadow-md';

const color = computed(() => {
  if (props?.disabled) return 'disabled text-base-disabled';
  const defaultColors =
    'bg-primary-blue-gradient text-light-50 hover:shadow-blue-500/60';
  if (!!props.type) {
    switch (props.type) {
      case 'primary':
        return defaultColors;
      case 'primary-alternative':
        return 'bg-primary-pink-gradient text-light-50 hover:shadow-pink-900/60';
      case 'secondary':
        return 'bg-stacked text-base-priority';
      case 'secondary-alternative':
        return 'bg-light-50 dark:bg-dark-700 text-base-priority';
      case 'tertiary':
        return 'bg-layer-secondary text-base-priority';
      case 'tertiary-alternative':
        return 'bg-base-secondary text-base-priority';
      case 'neutral':
        return 'bg-neutral-gradient dark:text-light-50';
      case 'other':
        return 'bg-gold-gradient text-light-50 hover:shadow-gold-500/60';
      case 'transparent':
        return 'text-base-priority';
    }
  }
  return defaultColors;
});

const padding = computed(() => {
  if (!!props.padding) {
    switch (props.padding) {
      case 'xs':
        return 'p-0.5';
      case 'sm':
        return 'p-1';
      case 'md':
        return 'p-2';
      case 'lg':
        return 'p-3';
      case 'xl':
        return 'p-4';
      case 'wide':
        return 'py-2 px-3';
      case 'wide-short':
        return 'py-1 px-3';
      case 'tall':
        return 'px-3 py-2';
      case 'none':
        return 'p-0';
    }
  }
  return 'p-2';
});

const rounding = computed(() => {
  if (props.shape === 'circle') return '';
  if (!!props.rounding) {
    switch (props.rounding) {
      case 'xs':
        return 'rounded-xs';
      case 'sm':
        return 'rounded-sm';
      case 'md':
        return 'rounded-md';
      case 'lg':
        return 'rounded-lg';
      case 'xl':
        return 'rounded-xl';
    }
  }
  return 'rounded-xl';
});

const shape = computed(() => {
  if (!!props.shape) {
    switch (props.shape) {
      case 'circle':
        return 'rounded-full aspect-square';
      case 'square':
        return 'aspect-square';
    }
  }
  return '';
});

const generatedClasses = computed(() => {
  let newDefaults = defaults;
  if ((props.class as string | string[]).includes('hidden')) {
    newDefaults = newDefaults.replace('flex', '');
  }
  return `design-system-button ${newDefaults} ${color.value} ${padding.value} ${rounding.value} ${shape.value} ${props.class}`;
});
</script>

<template>
  <slot v-if="!$slots.override" name="override" v-bind="$attrs" />
  <button
    v-if="!$slots.override"
    v-bind="$attrs"
    :type="nativeType"
    :class="generatedClasses"
    :disabled="props.disabled"
  >
    <slot />
  </button>
</template>

<style scoped lang="scss">
.disabled {
  @apply pointer-events-none cursor-not-allowed bg-dark-400;
}
</style>
